import { useState, useEffect, useCallback } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  Users,
  BedDouble,
  Ruler,
  MapPin,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
} from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { CORPORATE_HOMES } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* -------- Gallery -------- */
function Gallery({ images, title }) {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  const total = images.length;
  const next = useCallback(() => setIndex((i) => (i + 1) % total), [total]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + total) % total), [total]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(false);
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, next, prev]);

  const openAt = (i) => {
    setIndex(i);
    setLightbox(true);
  };

  return (
    <>
      {/* Desktop: hero + 2x2 grid */}
      <div className="hidden md:grid grid-cols-4 grid-rows-2 gap-2 aspect-[16/8] relative" data-testid="gallery-desktop">
        <button
          type="button"
          onClick={() => openAt(0)}
          className="col-span-2 row-span-2 relative overflow-hidden group bg-[#F1EFEA]"
          data-testid="gallery-hero-btn"
        >
          <img
            src={images[0]}
            alt={`${title} - hovedbillede`}
            className="w-full h-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.03]"
            loading="eager"
          />
        </button>
        {images.slice(1, 5).map((src, i) => (
          <button
            type="button"
            key={src + i}
            onClick={() => openAt(i + 1)}
            className="relative overflow-hidden group bg-[#F1EFEA]"
            data-testid={`gallery-thumb-${i + 1}`}
          >
            <img
              src={src}
              alt={`${title} - billede ${i + 2}`}
              className="w-full h-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.05]"
              loading="lazy"
            />
          </button>
        ))}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="absolute right-6 bottom-6 bg-white text-[#111111] text-[11px] tracking-[0.24em] uppercase px-5 py-2.5 border border-black/10 hover:bg-[#111111] hover:text-white transition-colors shadow-lg"
          data-testid="gallery-see-all-btn"
        >
          Vis alle billeder ({total})
        </button>
      </div>

      {/* Mobile: horizontal swipeable */}
      <div className="md:hidden -mx-6" data-testid="gallery-mobile">
        <div className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth no-scrollbar">
          {images.map((src, i) => (
            <button
              type="button"
              key={src + i}
              onClick={() => openAt(i)}
              className="snap-center shrink-0 w-[92%] mr-2 first:ml-6 last:mr-6 aspect-[4/3] overflow-hidden bg-[#F1EFEA]"
            >
              <img
                src={src}
                alt={`${title} - billede ${i + 1}`}
                className="w-full h-full object-cover"
                loading={i === 0 ? "eager" : "lazy"}
              />
            </button>
          ))}
        </div>
        <div className="mt-3 px-6 flex items-center justify-between">
          <span className="text-[11px] tracking-[0.28em] uppercase text-[#555555]">
            {total} billeder
          </span>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="text-[11px] tracking-[0.24em] uppercase text-[#111111] underline-offset-4 hover:underline"
          >
            Vis alle
          </button>
        </div>
      </div>

      {/* Full grid modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-[#0a0a0a] overflow-y-auto"
            data-testid="gallery-all-modal"
          >
            <div className="sticky top-0 z-10 bg-[#0a0a0a]/95 backdrop-blur border-b border-white/10 px-6 py-4 flex items-center justify-between">
              <span className="text-[11px] tracking-[0.28em] uppercase text-white/70">
                {title} · {total} billeder
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Luk"
                className="text-white hover:text-[#D4AF37] transition-colors"
                data-testid="gallery-close-all"
              >
                <X size={22} strokeWidth={1.5} />
              </button>
            </div>
            <div className="max-w-5xl mx-auto px-6 py-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {images.map((src, i) => (
                <button
                  type="button"
                  key={src + i}
                  onClick={() => {
                    setOpen(false);
                    openAt(i);
                  }}
                  className="relative aspect-[4/3] overflow-hidden bg-white/5"
                >
                  <img
                    src={src}
                    alt={`${title} - billede ${i + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-black/95 flex items-center justify-center"
            data-testid="gallery-lightbox"
            onClick={() => setLightbox(false)}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox(false);
              }}
              aria-label="Luk"
              className="absolute top-5 right-5 text-white hover:text-[#D4AF37]"
              data-testid="lightbox-close"
            >
              <X size={28} strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                prev();
              }}
              aria-label="Forrige"
              className="absolute left-4 sm:left-6 text-white hover:text-[#D4AF37] p-2"
            >
              <ChevronLeft size={36} strokeWidth={1.25} />
            </button>
            <img
              src={images[index]}
              alt={`${title} - billede ${index + 1}`}
              onClick={(e) => e.stopPropagation()}
              className="max-w-[92vw] max-h-[85vh] object-contain"
            />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                next();
              }}
              aria-label="Næste"
              className="absolute right-4 sm:right-6 text-white hover:text-[#D4AF37] p-2"
            >
              <ChevronRight size={36} strokeWidth={1.25} />
            </button>
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-xs tracking-[0.24em] uppercase">
              {index + 1} / {total}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/* -------- Inquiry form -------- */
function InquiryForm({ home }) {
  const [form, setForm] = useState({
    company: "",
    contact_name: "",
    email: "",
    phone: "",
    guests: "",
    check_in: "",
    check_out: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.company.trim() || !form.contact_name.trim() || !form.email.trim()) {
      toast.error("Udfyld venligst virksomhed, kontaktperson og e-mail.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        company: form.company.trim(),
        contact_name: form.contact_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        property_slug: home.slug,
        property_title: home.shortTitle,
        guests: form.guests ? Number(form.guests) : null,
        check_in: form.check_in || null,
        check_out: form.check_out || null,
        notes: form.notes.trim() || null,
      };
      await axios.post(`${API}/corporate-inquiry`, payload);
      toast.success("Tak - vi vender tilbage med tilgængelighed og pris hurtigst muligt.");
      setForm({
        company: "",
        contact_name: "",
        email: "",
        phone: "",
        guests: "",
        check_in: "",
        check_out: "",
        notes: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Kunne ikke sende forespørgslen. Prøv igen om et øjeblik.";
      toast.error(typeof detail === "string" ? detail : "Noget gik galt.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputBase =
    "w-full bg-transparent border-0 border-b border-white/20 focus:border-[#D4AF37] outline-none text-white text-lg placeholder-white/30 py-3 transition-colors";
  const labelBase = "block overline text-[10px] mb-2 text-[#D4AF37]";

  return (
    <section
      id="foresporg"
      data-testid="inquiry-section"
      className="bg-[#111111] text-white py-16 sm:py-28 relative overflow-hidden"
    >
      <div className="absolute -top-32 right-0 w-[34rem] h-[34rem] glow-gold pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="overline mb-5 text-[10px] sm:text-xs" style={{ color: "#D4AF37" }}>
              <span className="inline-block w-8 sm:w-12 h-px bg-[#D4AF37] align-middle mr-3 sm:mr-4" />
              Forespørg på boligen
            </p>
            <h2
              className="font-serif text-[1.75rem] sm:text-4xl lg:text-5xl font-light leading-[1.05] tracking-tight text-white"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Send en forespørgsel.{" "}
              <em className="italic text-[#D4AF37]">Vi bekræfter tilgængelighed &amp; pris</em>{" "}
              hurtigst muligt.
            </h2>
            <p className="mt-6 text-white/70 font-light leading-relaxed max-w-md">
              Boligen bookes ikke instant - vi vender tilbage med et konkret tilbud
              tilpasset opholdets længde og antal personer. Typisk svar samme dag.
            </p>
          </div>

          <form
            onSubmit={onSubmit}
            className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6"
            data-testid="inquiry-form"
          >
            <div className="sm:col-span-2">
              <label className={labelBase}>Virksomhed *</label>
              <input
                type="text"
                value={form.company}
                onChange={update("company")}
                placeholder="Firmanavn"
                className={inputBase}
                data-testid="form-company"
                required
              />
            </div>
            <div>
              <label className={labelBase}>Kontaktperson *</label>
              <input
                type="text"
                value={form.contact_name}
                onChange={update("contact_name")}
                placeholder="Fulde navn"
                className={inputBase}
                data-testid="form-contact-name"
                required
              />
            </div>
            <div>
              <label className={labelBase}>E-mail *</label>
              <input
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="navn@firma.dk"
                className={inputBase}
                data-testid="form-email"
                required
              />
            </div>
            <div>
              <label className={labelBase}>Telefon</label>
              <input
                type="tel"
                value={form.phone}
                onChange={update("phone")}
                placeholder="+45 ..."
                className={inputBase}
                data-testid="form-phone"
              />
            </div>
            <div>
              <label className={labelBase}>Antal personer</label>
              <input
                type="number"
                min="1"
                max="50"
                value={form.guests}
                onChange={update("guests")}
                placeholder="fx 2"
                className={inputBase}
                data-testid="form-guests"
              />
            </div>
            <div>
              <label className={labelBase}>Indflytningsdato</label>
              <input
                type="date"
                value={form.check_in}
                onChange={update("check_in")}
                className={`${inputBase} appearance-none [color-scheme:dark]`}
                data-testid="form-check-in"
              />
            </div>
            <div>
              <label className={labelBase}>Udflytningsdato</label>
              <input
                type="date"
                value={form.check_out}
                onChange={update("check_out")}
                className={`${inputBase} appearance-none [color-scheme:dark]`}
                data-testid="form-check-out"
              />
            </div>
            <div className="sm:col-span-2">
              <label className={labelBase}>Særlige ønsker</label>
              <textarea
                value={form.notes}
                onChange={update("notes")}
                placeholder="Fx antal senge, ekstra rengøring, parkering, EAN-fakturering ..."
                rows={4}
                className={`${inputBase} resize-none`}
                data-testid="form-notes"
              />
            </div>
            <div className="sm:col-span-2 pt-4 border-t border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-xs text-white/50 tracking-wide max-w-md">
                Ved at sende forespørgslen accepterer du, at vi gemmer dine
                oplysninger med henblik på at kontakte dig omkring boligen.
              </p>
              <button
                type="submit"
                disabled={submitting}
                data-testid="form-submit"
                className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#111111] px-8 py-4 text-xs tracking-[0.28em] uppercase font-medium hover:bg-[#C9A227] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Sender ..." : "Send forespørgsel"}
                <ArrowRight size={16} strokeWidth={1.5} />
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

/* -------- Main page -------- */
export default function FirmaboligDetail() {
  const { slug } = useParams();
  const home = CORPORATE_HOMES.find((h) => h.slug === slug);
  if (!home) return <Navigate to="/firmaboliger" replace />;

  return (
    <div className="bg-white text-[#222222]" data-testid={`firmabolig-detail-${home.slug}`}>
      <Header />

      <main className="pt-24 sm:pt-28">
        {/* Breadcrumb + gallery */}
        <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <nav className="mb-6 text-xs tracking-wide text-[#666]">
            <Link to="/firmaboliger" className="hover:text-[#C9A227] transition-colors">
              Firmaboliger
            </Link>
            <span className="mx-2 text-[#D4AF37]">/</span>
            <span className="text-[#111111]">{home.shortTitle}</span>
          </nav>
          <Gallery images={home.gallery} title={home.shortTitle} />
        </section>

        {/* Title + meta */}
        <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-10 sm:pt-14 pb-10 sm:pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-8">
              <p className="overline mb-4 text-[10px] sm:text-xs">
                <span className="inline-block w-8 h-px bg-[#D4AF37] align-middle mr-3" />
                Firmabolig · {home.area}
              </p>
              <h1
                className="font-serif text-[2rem] sm:text-5xl lg:text-6xl text-[#111111] font-light tracking-tight leading-[1.02]"
                style={{ fontFamily: "Fraunces, serif" }}
              >
                {home.title}
              </h1>
              <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-[#333333] text-sm">
                {home.guests && (
                  <span className="inline-flex items-center gap-2">
                    <Users size={16} strokeWidth={1.5} className="text-[#D4AF37]" />
                    Op til {home.guests} personer
                  </span>
                )}
                {home.sqm && (
                  <span className="inline-flex items-center gap-2">
                    <Ruler size={16} strokeWidth={1.5} className="text-[#D4AF37]" />
                    ca. {home.sqm} m²
                  </span>
                )}
                {home.bedrooms && (
                  <span className="inline-flex items-center gap-2">
                    <BedDouble size={16} strokeWidth={1.5} className="text-[#D4AF37]" />
                    {home.bedrooms}
                  </span>
                )}
                <span className="inline-flex items-center gap-2">
                  <MapPin size={16} strokeWidth={1.5} className="text-[#D4AF37]" />
                  {home.area}
                </span>
              </div>

              {home.headline && (
                <h2
                  className="font-serif text-[1.5rem] sm:text-3xl text-[#111111] font-light mt-14 leading-tight tracking-tight"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  {home.headline}
                </h2>
              )}
              <div className={`${home.headline ? "mt-6" : "mt-12"} text-[#333333] text-base sm:text-lg font-light leading-relaxed max-w-2xl space-y-4`}>
                {home.description.split("\n\n").map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>

              {/* Highlights */}
              {home.highlights?.length > 0 && (
                <ul className="mt-10 space-y-4">
                  {home.highlights.map((h) => (
                    <li
                      key={h}
                      className="flex items-start gap-4 text-[#333333] font-light leading-relaxed"
                    >
                      <Check size={20} className="text-[#D4AF37] mt-1 shrink-0" strokeWidth={1.5} />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Amenities sticky panel */}
            <aside className="lg:col-span-4">
              <div className="bg-[#F8F8F8] p-8 sm:p-10 lg:sticky lg:top-28">
                <p className="overline mb-6 text-[10px] sm:text-xs">
                  <span className="inline-block w-8 h-px bg-[#D4AF37] align-middle mr-3" />
                  {home.amenities?.length ? "Faciliteter" : "Forespørg"}
                </p>
                {home.amenities?.length ? (
                  <ul className="space-y-3">
                    {home.amenities.map((a) => (
                      <li
                        key={a}
                        className="flex items-center gap-3 text-[#111111] font-light"
                        data-testid="amenity-item"
                      >
                        <Check size={16} className="text-[#D4AF37] shrink-0" strokeWidth={1.5} />
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-[#555555] font-light leading-relaxed" data-testid="amenities-pending">
                    Kontakt os for detaljer om størrelse, antal personer og faciliteter.
                  </p>
                )}
                <a
                  href="#foresporg"
                  className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-[#111111] text-white px-6 py-3.5 text-xs tracking-[0.28em] uppercase hover:bg-[#D4AF37] hover:text-[#111111] transition-colors"
                  data-testid="scroll-to-inquiry-btn"
                >
                  Send forespørgsel
                  <ArrowRight size={14} strokeWidth={1.5} />
                </a>
              </div>
            </aside>
          </div>
        </section>

        {/* Inquiry form */}
        <InquiryForm home={home} />
      </main>

      <Footer />
    </div>
  );
}
